import { useState } from 'react';

import { BLANK_SHIRT_PATH } from '@lib/utils';

type PlayerKitProps = {
  /** Team short name; used for the image alt text and e2e hooks. */
  teamShortName: string;
  /**
   * CDN-hosted kit image from the ffh-backend API. The backend already picks
   * the goalkeeper variant, so no position-based swapping happens here. When
   * absent (enrichment still loading) or when it fails to load, the bundled
   * blank shirt is shown instead.
   */
  kitImageUrl?: string;
  className?: string;
};

/**
 * Renders a player's team kit from the backend-provided CDN image, falling
 * back to the bundled blank shirt when no URL is available or the image fails
 * to load. Mirrors scoreline-app's PlayerImage: the backend is the source of
 * truth for kit artwork, so the web no longer resolves bundled per-team shirts.
 */
export const PlayerKit = ({
  teamShortName,
  kitImageUrl,
  className
}: PlayerKitProps) => {
  // Remember the source that failed rather than a bare boolean so a kitImageUrl
  // that changes later (e.g. new season) is attempted again instead of staying
  // stuck on the blank shirt.
  const [failedSource, setFailedSource] = useState<string | null>(null);

  const source =
    kitImageUrl && kitImageUrl !== failedSource
      ? kitImageUrl
      : BLANK_SHIRT_PATH;

  return (
    <img
      className={className ?? ''}
      src={source}
      alt={`${teamShortName} shirt`}
      loading="lazy"
      decoding="async"
      onError={() => setFailedSource(kitImageUrl ?? null)}
      data-cy="player-shirt"
      data-cy-team-code={teamShortName}
    />
  );
};
